import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, collection, query, orderBy, setDoc, addDoc, serverTimestamp, limit, startAfter, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext'; 
import { initialResumeReports, initialBudgetData } from '../constants/initialStates';
import { initialProgress, professionalismModuleContent } from '../constants/modules';

const CurriculumContext = createContext();

export const useCurriculum = () => useContext(CurriculumContext);

export const CurriculumProvider = ({ children }) => {
  const { authUser, userProfile } = useAuth() || {}; 

  const [resumeReports, setResumeReports] = useState([]);
  const [budgetData, setBudgetData] = useState(initialBudgetData);
  const [pro101Progress, setPro101Progress] = useState(initialProgress);

  useEffect(() => {
    if (!authUser) {
        setResumeReports([]);
        setBudgetData(initialBudgetData);
        setPro101Progress(initialProgress);
        return;
    }

    // CLAMPED STREAM: Only listen to the latest 3 documents to drastically reduce read payloads
    const q = query(
        collection(db, 'users', authUser.uid, 'resume_reports'), 
        orderBy('timestamp', 'desc'), 
        limit(3)
    );
    
    const unsubReports = onSnapshot(q, (snap) => {
        const reports = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setResumeReports(reports);
    });

    const unsubBudget = onSnapshot(doc(db, 'users', authUser.uid, 'components', 'budget'), (snap) => {
        if (snap.exists()) setBudgetData(snap.data());
    });

    const unsubPro = onSnapshot(doc(db, 'users', authUser.uid, 'components', 'pro101'), (snap) => {
        if (snap.exists()) setPro101Progress(snap.data());
    });

    return () => {
        unsubReports();
        unsubBudget();
        unsubPro();
    };
  }, [authUser]);

  // LAZY LOAD FALLBACK
  const fetchHistoricalReports = async (lastVisibleDoc) => {
      if (!authUser) return [];
      try {
          let histQuery = query(collection(db, 'users', authUser.uid, 'resume_reports'), orderBy('timestamp', 'desc'), limit(5));
          if (lastVisibleDoc) {
              histQuery = query(collection(db, 'users', authUser.uid, 'resume_reports'), orderBy('timestamp', 'desc'), startAfter(lastVisibleDoc), limit(5));
          }
          const snap = await getDocs(histQuery);
          return snap.docs;
      } catch (e) {
          console.error("Failed to lazy load historical reports:", e);
          return [];
      }
  };

  const step1Completed = resumeReports.length > 0 && resumeReports[0].overall_score > 0;
  const step2Completed = professionalismModuleContent.every(part => pro101Progress[part.id]);
  const step3Completed = budgetData.salary && budgetData.takeHome > 0;

  const createNewResumeReport = async (data) => {
     if (!authUser) return null;
     const docRef = await addDoc(collection(db, 'users', authUser.uid, 'resume_reports'), { ...data, timestamp: serverTimestamp() });
     return docRef;
  };

  const updateLatestResumeReport = async (data) => {
    if (!authUser || resumeReports.length === 0) return;
    await setDoc(doc(db, 'users', authUser.uid, 'resume_reports', resumeReports[0].id), data, { merge: true });
  };

  const updateBudgetData = async (data) => {
    if (!authUser) return;
    await setDoc(doc(db, 'users', authUser.uid, 'components', 'budget'), data, { merge: true });
  };

  const updatePro101Progress = async (data) => {
    if (!authUser) return;
    await setDoc(doc(db, 'users', authUser.uid, 'components', 'pro101'), data, { merge: true });
  };

  const saveAiAnalysisToSubCollection = async (reportId, analysisType, data) => {
    if (!authUser) return;
    try {
        const analysisRef = doc(
            db, 
            'users', authUser.uid, 
            'resume_reports', reportId, 
            'ai_analyses', analysisType
        );
        
        await setDoc(analysisRef, {
            ...data,
            type: analysisType,
            // --- O(1) DENORMALIZATION PATCH ---
            enterpriseTenantId: userProfile?.enterpriseTenantId || 'public',
            timestamp: serverTimestamp()
        }, { merge: true });
    } catch (e) {
        console.error("Sub-collection write failed:", e);
    }
  };

  return (
    <CurriculumContext.Provider value={{
      resumeReports, budgetData, pro101Progress,
      step1Completed, step2Completed, step3Completed,
      createNewResumeReport, updateLatestResumeReport, updateBudgetData, updatePro101Progress,
      saveAiAnalysisToSubCollection, fetchHistoricalReports
    }}>
      {children}
    </CurriculumContext.Provider>
  );
};